import { IDENTITY_CONFIG, METADATA_OIDC } from "../utils/AuthConst";
import { UserManager, WebStorageStateStore, Log, User } from "oidc-client";
import {
  client_id_external,
  generateToken,
  refreshToken,
  scopes_external,
} from "./api/auth/auth-service";
import { setUserLocalStorage } from "./AuthService";

export default class ExternalLoginService {
  userManager = null;
  opaqueToken = null;
  accessToken = null;
  _queryParamsTokenKey = "_token";
  refreshTimeout = null;

  constructor() {
    this._logConf();
  }

  async serviceStart() {
    this.startUserManager();
    const isAutenticated = await this.isAuthenticated();

    if (!isAutenticated) {
      return this._init();
    } else {
      const user = await this.userManager.getUser();
      this.scheduleTokenRefresh(user);

      return isAutenticated;
    }
  }

  async auth(opaqueToken) {
    try {
      const { data } = await generateToken(opaqueToken);
      const user = new User({
        id_token: data.access_token,
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        token_type: "Bearer",
        scope: IDENTITY_CONFIG.scope,
        profile: this.parseJwt(data.access_token),
        expires_at: Math.floor(Date.now() / 1000) + data.expires_in,
      });

      await this.userManager.storeUser(user);

      this.setUserToken(user);
      this.scheduleTokenRefresh(user);
      this.removeTokenParams();
      return true;
    } catch (error) {
      localStorage.clear();
      console.error("Failed to obtain the token with opaque token", error);
      return false;
    }
  }

  async isAuthenticated() {
    const accessToken = localStorage.getItem("access_token");
    const user = await this.userManager.getUser();

    if (!accessToken || !user) {
      return false;
    }

    try {
      const expiresAt = user.expires_at * 1000;

      const expired = expiresAt <= Date.now();

      if (expired) {
        await this.logout();
        return false;
      }

      return true;
    } catch (error) {
      return false;
    }
  }

  parseJwt(token) {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace("-", "+").replace("_", "/");
    return JSON.parse(window.atob(base64));
  }

  signoutRedirectCallback = () => {
    this.userManager
      .signoutRedirectCallback()
      .then(console.log)
      .catch(console.error);
  };

  async logout() {
    try {
      if (this.userManager)
        await this.userManager.signoutRedirect({
          id_token_hint: localStorage.getItem("id_token"),
        });
    } catch (error) {
      console.error("[AUTH] Logout error", error);
    }
    localStorage.clear();
    window.location.reload();
  }

  async refreshToken() {
    try {
      const user = await this.userManager.getUser();

      if (!user || !user.refresh_token) {
        console.warn("[AUTH] No refresh token available");
        localStorage.clear();
        return false;
      }

      const { data } = await refreshToken(user);

      const refreshedUser = new User({
        ...user,
        access_token: data.access_token,
        refresh_token: data.refresh_token || user.refresh_token,
        id_token: data.id_token || user.id_token,
        token_type: data.token_type || user.token_type,
        expires_at: Math.floor(Date.now() / 1000) + data.expires_in,
      });

      await this.userManager.storeUser(refreshedUser);

      this.setUserToken(refreshedUser);
      this.scheduleTokenRefresh(refreshedUser);

      return true;
    } catch (error) {
      console.error("[AUTH] Refresh token failed", error);
      this.logout();

      return false;
    }
  }

  scheduleTokenRefresh(user) {
    const expiresAt = user.expires_at * 1000;
    const now = Date.now();
    const refreshBefore = 60 * 1000;
    const delay = Math.max(expiresAt - now - refreshBefore, 0);

    clearTimeout(this.refreshTimeout);

    this.refreshTimeout = setTimeout(async () => {
      await this.refreshToken();
    }, delay);
  }

  setUserToken(user) {
    setUserLocalStorage(user);
  }

  _init() {
    this._loadSearchParams();

    if (!this.opaqueToken) return false;

    return this.auth(this.opaqueToken);
  }

  startUserManager() {
    this.userManager = new UserManager({
      ...IDENTITY_CONFIG,
      scope: scopes_external,
      client_id: client_id_external,
      automaticSilentRenew: false,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      metadata: {
        ...METADATA_OIDC,
      },
    });
  }

  _logConf() {
    Log.logger = console;
    Log.level = process.env.NODE_ENV === "production" ? Log.ERROR : Log.DEBUG;
  }

  _loadSearchParams() {
    const hash = window.location.hash;
    const queryString = hash.includes("?") ? hash.split("?")[1] : "";

    const urlParams = new URLSearchParams(queryString);
    this.opaqueToken = urlParams.get(this._queryParamsTokenKey);
  }

  removeTokenParams() {
    const hash = window.location.hash;
    const [path, queryString] = hash.split("?");

    if (!queryString) return;

    const params = new URLSearchParams(queryString);

    if (params.has(this._queryParamsTokenKey)) {
      params.delete(this._queryParamsTokenKey);

      const newQuery = params.toString();
      const newHash = newQuery ? `${path}?${newQuery}` : path;
      const newUrl = `${window.location.pathname}${newHash}`;

      window.history.replaceState({}, document.title, newUrl);
    }
  }
}
